// RolesPage.jsx — الأدوار والصلاحيات (بالتصميم الموحّد)
import React, { useState, useEffect, useMemo, useCallback, forwardRef } from 'react';
import rolesApi from '../../api/rolesApi';
import {
  Box, Paper, Typography, TextField, Button, IconButton, Tooltip, Chip, Dialog, Slide, Checkbox,
  InputAdornment, LinearProgress, CircularProgress, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SearchIcon from '@mui/icons-material/Search';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Collapse from '@mui/material/Collapse';
import { toast } from 'react-toastify';
import { ReportPage, fieldSx, BRAND } from '../../components/Reports/ReportKit';

const BORDER = '#e5e7eb';

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ───────────────────────── أسماء عربية للصلاحيات ─────────────────────────
const ACTIONS = {
  Create: 'إنشاء', Add: 'إضافة', Edit: 'تعديل', Update: 'تحديث', Delete: 'حذف', Remove: 'إزالة',
  View: 'عرض', Read: 'قراءة', List: 'عرض القائمة', Access: 'دخول', Print: 'طباعة', Approve: 'اعتماد',
  Unapprove: 'إلغاء الاعتماد', Reject: 'رفض', Export: 'تصدير', Import: 'استيراد', Manage: 'إدارة',
  Post: 'ترحيل', Unpost: 'إلغاء الترحيل', Cancel: 'إلغاء', Return: 'مرتجع', Close: 'إقفال', Open: 'فتح',
  Transfer: 'تحويل', Move: 'نقل', Sync: 'مزامنة', Send: 'إرسال', Discount: 'خصم', Price: 'تعديل السعر',
  Run: 'تشغيل', VOUCHER_VIEW: 'عرض',
};
const CATEGORIES = {
  Invoice: 'الفواتير', Customer: 'العملاء', Vendor: 'الموردين', Supplier: 'الموردين', Bank: 'البنوك',
  Safe: 'الصناديق', Bond: 'السندات', Voucher: 'السندات', Product: 'المنتجات', Category: 'التصنيفات',
  Unit: 'الوحدات', Stock: 'المخزون', Warehouse: 'المستودعات', Branch: 'الفروع', Company: 'المنشأة',
  User: 'المستخدمين', Role: 'الأدوار', Security: 'الأمان', Report: 'التقارير', Reports: 'التقارير',
  Setting: 'الإعدادات', Settings: 'الإعدادات', GISettings: 'الإعدادات العامة', ChartOfAccounts: 'شجرة الحسابات',
  JournalEntry: 'القيود اليومية', CostCenter: 'مراكز التكلفة', Pos: 'نقاط البيع', POS: 'نقاط البيع',
  Zatca: 'الفوترة الإلكترونية', Dashboard: 'لوحة التحكم', Salesman: 'المندوبين', Tax: 'الضرائب',
  'Invoice.Sales': 'فواتير المبيعات', 'Invoice.Purchase': 'فواتير المشتريات', 'Invoice.Quotation': 'عروض الأسعار',
  'Invoice.PurchaseReturn': 'مرتجع المشتريات', 'Invoice.SalesReturn': 'مرتجع المبيعات', 'Invoice.OpeningStock': 'رصيد افتتاحي',
  'Invoice.StockIn': 'إدخال مخزني', 'Invoice.StockOut': 'إخراج مخزني', 'Invoice.Transfer': 'مناقلة',
  'Bond.Receipt': 'سندات القبض', 'Bond.Payment': 'سندات الصرف', 'Bond.Tax': 'سند ضريبة',
  Promotion: 'العروض الترويجية', InvoiceType: 'أنواع الفواتير', PrintLayout: 'تصميم الطباعة', Import: 'الاستيراد',
  PaymentMethod: 'طرق الدفع', GlSettings: 'توجيه الحسابات', VOUCHER_VIEW: 'السندات (عرض)',
  'Pos.Devices': 'أجهزة نقطة البيع', 'Pos.Session': 'ورديات نقطة البيع',
  'Report.AccountStatement': 'كشف الحساب', 'Report.TrialBalance': 'ميزان المراجعة', 'Report.BalanceSheet': 'الميزانية العمومية',
  'Report.IncomeStatement': 'قائمة الدخل', 'Report.Vat': 'الإقرار الضريبي', 'Report.Profit': 'تقارير الأرباح',
  'Report.Inventory': 'تقارير المخزون',
};
const actionName = (perm) => { const a = perm.split('.').pop(); return ACTIONS[a] || a; };
const categoryOf = (perm) => {
  const p = perm.split('.');
  return ['Invoice', 'Bond', 'Pos', 'Report'].includes(p[0]) && p.length > 2 ? `${p[0]}.${p[1]}` : p[0];
};
const categoryName = (c) => CATEGORIES[c] || c;

// ───────────────────────── التسلسل الهرمي: القسم ← المجموعة ← الصلاحية ─────────────────────────
// كل قسم يضم مجموعاته بالترتيب؛ أي مجموعة غير مذكورة تذهب لـ "أخرى" (مثل الصلاحيات المضافة يدويًا)
const MODULES = [
  { key: 'sales', name: 'المبيعات', icon: <StorefrontOutlinedIcon />, color: '#4F46E5',
    groups: ['Invoice.Sales', 'Invoice.SalesReturn', 'Invoice.Quotation', 'Customer', 'Bond.Receipt'] },
  { key: 'purchases', name: 'المشتريات', icon: <ShoppingCartOutlinedIcon />, color: '#E11D48',
    groups: ['Invoice.Purchase', 'Invoice.PurchaseReturn', 'Supplier', 'Bond.Payment'] },
  { key: 'pos', name: 'نقطة البيع', icon: <PointOfSaleOutlinedIcon />, color: '#7C3AED',
    groups: ['Pos.Session', 'Pos.Devices', 'Promotion'] },
  { key: 'stock', name: 'المستودعات', icon: <WarehouseOutlinedIcon />, color: '#059669',
    groups: ['Product', 'Category', 'Unit', 'Stock', 'Invoice.OpeningStock', 'Invoice.StockIn', 'Invoice.StockOut', 'Invoice.Transfer', 'Import', 'Report.Inventory'] },
  { key: 'accounting', name: 'المحاسبة والمالية', icon: <AccountBalanceOutlinedIcon />, color: '#2563EB',
    groups: ['ChartOfAccounts', 'JournalEntry', 'GlSettings', 'Bond.Tax', 'VOUCHER_VIEW', 'Safe', 'Bank', 'PaymentMethod', 'CostCenter'] },
  { key: 'reports', name: 'التقارير', icon: <InsightsOutlinedIcon />, color: '#0891B2',
    groups: ['Report', 'Report.AccountStatement', 'Report.TrialBalance', 'Report.IncomeStatement', 'Report.BalanceSheet', 'Report.Vat', 'Report.Profit'] },
  { key: 'settings', name: 'الإعدادات والمنشأة', icon: <TuneOutlinedIcon />, color: '#D97706',
    groups: ['Settings', 'Company', 'Branch', 'Zatca', 'InvoiceType', 'PrintLayout', 'Invoice'] },
  { key: 'security', name: 'المستخدمون والأمان', icon: <ManageAccountsOutlinedIcon />, color: '#475569',
    groups: ['User', 'Role', 'Security'] },
];
const OTHER_MODULE = { key: 'other', name: 'أخرى', icon: <MoreHorizIcon />, color: '#64748B', groups: [] };
const GROUP_TO_MODULE = Object.fromEntries(MODULES.flatMap(m => m.groups.map(g => [g, m.key])));

// ترتيب الصلاحيات داخل المجموعة: عرض ثم إنشاء ثم تعديل...
const ACTION_ORDER = ['View', 'VOUCHER_VIEW', 'Create', 'Add', 'Edit', 'Update', 'Delete', 'Print', 'Approve', 'Unapprove', 'Post', 'Move', 'Run', 'Manage'];
const actionRank = (p) => { const i = ACTION_ORDER.indexOf(p.split('.').pop()); return i < 0 ? 99 : i; };

// ======================================================================
// الصلاحية يفحصها السيرفر (Role.Manage أو Security.Manage أو مالك الشركة) — الصفحة تعرض الرفض إن جاء 403
// (كان الفحص هنا باسم المستخدم "owner" وأي مستخدم يغيّر اسمه إلى owner كان يدخل)
export default function RolesPage() {
  const [denied, setDenied] = useState(false);
  const onDenied = useCallback(() => setDenied(true), []);
  if (denied) return <NotAuthorized />;
  return <RolesManager onDenied={onDenied} />;
}

function NotAuthorized() {
  return (
    <Box dir="rtl" sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh', p: 3 }}>
      <Paper elevation={0} sx={{ p: 4, maxWidth: 440, textAlign: 'center', borderRadius: '18px', border: `1px solid ${BORDER}` }}>
        <Box sx={{ width: 72, height: 72, mx: 'auto', mb: 2, borderRadius: '20px', display: 'grid', placeItems: 'center', bgcolor: BRAND.primarySoft, color: BRAND.primary }}>
          <LockOutlinedIcon sx={{ fontSize: 36 }} />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 18, mb: 1 }}>ليس لديك صلاحية للوصول</Typography>
        <Typography sx={{ color: '#64748b', fontSize: 14, lineHeight: 1.8 }}>
          هذه الصفحة مخصصة لمالك المنشأة أو لمن يملك صلاحية إدارة الأدوار.
        </Typography>
      </Paper>
    </Box>
  );
}

// ======================================================================
function RolesManager({ onDenied }) {
  const companyId = parseInt(localStorage.getItem('companyId'), 10);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [editor, setEditor] = useState(null); // { id, name, description, permissions, readOnly }

  const fetchRoles = useCallback(async () => {
    try {
      const res = await rolesApi.getAll(companyId);
      setRoles(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      if (e?.response?.status === 403) { onDenied(); return; }
      toast.error('فشل تحميل الأدوار');
    }
  }, [companyId, onDenied]);

  useEffect(() => {
    Promise.all([
      fetchRoles(),
      rolesApi.getPermissions().then(r => setPermissions(Array.isArray(r.data) ? r.data : [])).catch(console.error),
    ]).finally(() => setLoading(false));
  }, [fetchRoles]);

  const sync = async () => {
    setSyncing(true);
    try {
      await rolesApi.syncPermissions();
      toast.success('تم تحديث صلاحيات الأدوار النظامية');
      await fetchRoles();
      const r = await rolesApi.getPermissions().catch(() => null);
      if (Array.isArray(r?.data)) setPermissions(r.data);
    } catch {
      toast.error('فشل تحديث الصلاحيات');
    } finally {
      setSyncing(false);
    }
  };

  const remove = async (role) => {
    if (!window.confirm(`هل أنت متأكد من حذف الدور "${role.name}"؟`)) return;
    try {
      await rolesApi.delete(role.id);
      toast.success('تم حذف الدور');
      fetchRoles();
    } catch (e) {
      const d = e?.response?.data;
      toast.error((typeof d === 'string' && d) || d?.message || 'لا يمكن حذف هذا الدور');
    }
  };

  const openNew = () => setEditor({ id: null, name: '', description: '', permissions: [] });
  const openEdit = (r) => setEditor({ id: r.id, name: r.name, description: r.description || '', permissions: [...(r.permissions || [])], readOnly: !!r.isSystemRole });
  const openCopy = (r) => setEditor({ id: null, name: `${r.name} (نسخة)`, description: r.description || '', permissions: [...(r.permissions || [])] });

  const total = permissions.length;
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? roles.filter(r => `${r.name} ${r.description || ''}`.toLowerCase().includes(q)) : roles;
  }, [roles, search]);

  return (
    <ReportPage
      title="الأدوار والصلاحيات"
      subtitle="حدد ما يستطيع كل دور عرضه وتنفيذه في النظام"
      icon={<AdminPanelSettingsOutlinedIcon />}
      actions={[
        { label: syncing ? 'جاري التحديث...' : 'تحديث صلاحيات النظام', icon: syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />, onClick: sync, disabled: syncing },
        { label: 'دور جديد', icon: <AddIcon />, onClick: openNew, primary: true },
      ]}
      stats={[
        { label: 'عدد الأدوار', value: roles.length, icon: <GroupsOutlinedIcon />, color: 'primary' },
        { label: 'أدوار النظام', value: roles.filter(r => r.isSystemRole).length, icon: <GppGoodOutlinedIcon />, color: 'info' },
        { label: 'أدوار مخصصة', value: roles.filter(r => !r.isSystemRole).length, icon: <AdminPanelSettingsOutlinedIcon />, color: 'success' },
        { label: 'إجمالي الصلاحيات', value: total, icon: <KeyOutlinedIcon />, color: 'warning' },
      ]}
    >
      <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: '14px', p: 1.25, mb: 1.5 }}>
        <TextField size="small" fullWidth placeholder="بحث باسم الدور أو الوصف..." value={search} onChange={e => setSearch(e.target.value)} sx={fieldSx}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
      </Paper>

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress sx={{ color: BRAND.primary }} /></Box>
      ) : filtered.length === 0 ? (
        <Paper elevation={0} sx={{ border: `1px dashed #cbd5e1`, borderRadius: '14px', py: 6, textAlign: 'center', color: '#64748b' }}>
          <GroupsOutlinedIcon sx={{ fontSize: 40, color: '#cbd5e1' }} />
          <Typography sx={{ fontWeight: 700, mt: 1 }}>{search ? 'لا توجد نتائج' : 'لا توجد أدوار مضافة'}</Typography>
          {!search && <Button onClick={openNew} startIcon={<AddIcon />} sx={{ mt: 1.5, fontWeight: 700, '& .MuiButton-startIcon': { ml: .5, mr: -.25 } }}>إضافة أول دور</Button>}
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' } }}>
          {filtered.map(r => {
            const count = (r.permissions || []).length;
            const pct = total ? Math.round((count / total) * 100) : 0;
            return (
              <Paper key={r.id} elevation={0} sx={{
                border: `1px solid ${BORDER}`, borderRadius: '14px', p: 1.75, display: 'flex', flexDirection: 'column', gap: 1.25,
                transition: '.15s', '&:hover': { borderColor: BRAND.primaryLight, boxShadow: '0 8px 22px -14px rgba(79,70,229,.45)' },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '11px', flexShrink: 0, display: 'grid', placeItems: 'center',
                    bgcolor: r.isSystemRole ? '#f1f5f9' : BRAND.primarySoft, color: r.isSystemRole ? '#475569' : BRAND.primary }}>
                    {r.isSystemRole ? <GppGoodOutlinedIcon /> : <AdminPanelSettingsOutlinedIcon />}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: .75 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: 15 }} noWrap>{r.name}</Typography>
                      {r.isSystemRole && <Chip label="نظام" size="small" sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />}
                    </Box>
                    <Typography sx={{ fontSize: 12.5, color: '#64748b', minHeight: 20 }} noWrap>{r.description || '—'}</Typography>
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: .5 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#334155' }}>{count} صلاحية</Typography>
                    <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>{pct}%</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 6, bgcolor: '#eef2ff', '& .MuiLinearProgress-bar': { borderRadius: 6, bgcolor: BRAND.primary } }} />
                </Box>
                <Box sx={{ display: 'flex', gap: .5, mt: 'auto', pt: .5, borderTop: '1px dashed #eef0f4' }}>
                  {r.isSystemRole ? (
                    <Button size="small" startIcon={<VisibilityOutlinedIcon />} onClick={() => openEdit(r)} sx={{ fontWeight: 700, '& .MuiButton-startIcon': { ml: .5, mr: -.25 } }}>عرض</Button>
                  ) : (
                    <Button size="small" startIcon={<EditOutlinedIcon />} onClick={() => openEdit(r)} sx={{ fontWeight: 700, '& .MuiButton-startIcon': { ml: .5, mr: -.25 } }}>تعديل</Button>
                  )}
                  <Box sx={{ flex: 1 }} />
                  <Tooltip title="نسخ كدور جديد"><IconButton size="small" onClick={() => openCopy(r)}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                  {!r.isSystemRole && (
                    <Tooltip title="حذف"><IconButton size="small" onClick={() => remove(r)} sx={{ color: '#e11d48' }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                  )}
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      {editor && (
        <RoleEditor
          initial={editor}
          allPermissions={permissions}
          companyId={companyId}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); fetchRoles(); }}
          onAddPermission={(p) => setPermissions(prev => (prev.includes(p) ? prev : [...prev, p]))}
        />
      )}
    </ReportPage>
  );
}

// ======================================================================
function RoleEditor({ initial, allPermissions, companyId, onClose, onSaved, onAddPermission }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [show, setShow] = useState('all'); // all | on | off
  const [newPerm, setNewPerm] = useState('');
  const ro = !!initial.readOnly;
  const selected = useMemo(() => new Set(form.permissions), [form.permissions]);

  // القسم ← المجموعة ← الصلاحيات (مع البحث والفلتر)
  const modules = useMemo(() => {
    const q = query.trim().toLowerCase();
    const byGroup = {};
    allPermissions.forEach(p => {
      const c = categoryOf(p);
      const mod = MODULES.find(m => m.key === GROUP_TO_MODULE[c]) || OTHER_MODULE;
      const hay = `${p} ${actionName(p)} ${categoryName(c)} ${mod.name}`.toLowerCase();
      if (q && !hay.includes(q)) return;
      if (show === 'on' && !selected.has(p)) return;
      if (show === 'off' && selected.has(p)) return;
      (byGroup[c] = byGroup[c] || []).push(p);
    });
    const result = [...MODULES, OTHER_MODULE].map(m => {
      const groupKeys = m.key === 'other'
        ? Object.keys(byGroup).filter(g => !GROUP_TO_MODULE[g]).sort((a, b) => categoryName(a).localeCompare(categoryName(b), 'ar'))
        : m.groups.filter(g => byGroup[g]);
      const groupsList = groupKeys.map(g => [g, [...byGroup[g]].sort((a, b) => actionRank(a) - actionRank(b))]);
      return { ...m, groups: groupsList, perms: groupsList.flatMap(([, ps]) => ps) };
    });
    return result.filter(m => m.perms.length > 0);
  }, [allPermissions, query, show, selected]);

  // الأقسام مغلقة افتراضيًا وتُفتح بالضغط — البحث يفتح الكل
  const [expanded, setExpanded] = useState(() => new Set());
  const searching = !!query.trim() || show !== 'all';
  const isOpen = (key) => searching || expanded.has(key);
  const toggleModule = (key) => setExpanded(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const collapseAll = () => setExpanded(new Set());
  const expandAll = () => setExpanded(new Set(modules.map(m => m.key)));
  const jumpTo = (key) => {
    setExpanded(prev => { const n = new Set(prev); n.add(key); return n; });
    requestAnimationFrame(() => document.getElementById(`perm-mod-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  const setPerms = (fn) => setForm(f => ({ ...f, permissions: fn(new Set(f.permissions)) }));
  const toggle = (p) => !ro && setPerms(s => { if (s.has(p)) s.delete(p); else s.add(p); return [...s]; });
  const setGroup = (perms, on) => !ro && setPerms(s => { perms.forEach(p => (on ? s.add(p) : s.delete(p))); return [...s]; });

  const addCustom = () => {
    const p = newPerm.trim();
    if (!p) return;
    onAddPermission(p);
    setPerms(s => { s.add(p); return [...s]; });
    setNewPerm('');
    toast.success(`تمت إضافة الصلاحية: ${p}`);
  };

  const save = useCallback(async () => {
    if (ro) return;
    if (!form.name.trim()) { toast.warning('يرجى إدخال اسم الدور'); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), description: form.description, permissions: form.permissions, companyId };
      if (form.id) await rolesApi.update(form.id, payload);
      else await rolesApi.create(payload);
      toast.success(form.id ? 'تم تحديث الدور' : 'تم إنشاء الدور');
      onSaved();
    } catch (e) {
      const d = e?.response?.data;
      toast.error((typeof d === 'string' && d) || d?.message || 'حدث خطأ أثناء الحفظ');
    } finally {
      setSaving(false);
    }
  }, [form, companyId, ro, onSaved]);

  useEffect(() => {
    const onKey = (e) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); if (!saving) save(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [save, saving]);

  const total = allPermissions.length;
  const count = form.permissions.length;

  return (
    <Dialog open fullScreen onClose={onClose} TransitionComponent={Transition} dir="rtl" PaperProps={{ sx: { bgcolor: '#f1f5f9' } }}>
      {/* الرأس */}
      <Box sx={{ position: 'sticky', top: 0, zIndex: 3, display: 'flex', alignItems: 'center', gap: 1.5, px: { xs: 1.5, sm: 3 }, py: 1.5, color: '#fff', background: BRAND.headerGradient, boxShadow: BRAND.headerShadow }}>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 18 }} noWrap>
            {ro ? `عرض الدور: ${form.name}` : form.id ? `تعديل الدور: ${form.name || ''}` : 'دور جديد'}
          </Typography>
          <Typography sx={{ fontSize: 13, opacity: .85 }}>{count} من {total} صلاحية {ro ? '— دور نظامي للعرض فقط' : ''}</Typography>
        </Box>
        {!ro && (
          <Button onClick={save} disabled={saving} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
            sx={{ bgcolor: '#fff', color: BRAND.primaryDark, fontWeight: 800, borderRadius: '11px', px: 2.5, '&:hover': { bgcolor: BRAND.primarySoft }, '& .MuiButton-startIcon': { ml: .75, mr: -.25 } }}>
            {saving ? 'جاري الحفظ...' : 'حفظ'}
          </Button>
        )}
      </Box>

      <Box sx={{ p: { xs: 1.5, sm: 3 }, display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', lg: '320px minmax(0,1fr)' }, alignItems: 'start' }}>
        {/* بيانات الدور */}
        <Box sx={{ display: 'grid', gap: 1.5, position: { lg: 'sticky' }, top: { lg: 88 } }}>
          <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: '14px', p: 2, display: 'grid', gap: 1.5 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 15 }}>بيانات الدور</Typography>
            <TextField size="small" label="اسم الدور *" value={form.name} disabled={ro} sx={fieldSx}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} autoFocus={!form.id} />
            <TextField size="small" label="الوصف" value={form.description} disabled={ro} multiline minRows={2} sx={fieldSx}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: .5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>الصلاحيات الممنوحة</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: BRAND.primary, direction: 'ltr' }}>{count} / {total}</Typography>
              </Box>
              <LinearProgress variant="determinate" value={total ? (count / total) * 100 : 0}
                sx={{ height: 8, borderRadius: 8, bgcolor: '#eef2ff', '& .MuiLinearProgress-bar': { borderRadius: 8, bgcolor: BRAND.primary } }} />
            </Box>
            {!ro && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button fullWidth variant="outlined" size="small" startIcon={<DoneAllIcon />} onClick={() => setForm(f => ({ ...f, permissions: [...allPermissions] }))}
                  sx={{ borderRadius: '10px', fontWeight: 700, '& .MuiButton-startIcon': { ml: .5, mr: -.25 } }}>منح الكل</Button>
                <Button fullWidth variant="outlined" color="error" size="small" startIcon={<RemoveDoneIcon />} onClick={() => setForm(f => ({ ...f, permissions: [] }))}
                  sx={{ borderRadius: '10px', fontWeight: 700, '& .MuiButton-startIcon': { ml: .5, mr: -.25 } }}>إلغاء الكل</Button>
              </Box>
            )}
          </Paper>

          {!ro && (
            <Paper elevation={0} sx={{ border: '1px dashed #cbd5e1', borderRadius: '14px', p: 2, display: 'grid', gap: 1 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 13.5 }}>إضافة صلاحية يدويًا</Typography>
              <TextField size="small" placeholder="مثال: Invoice.Type.Special" value={newPerm} sx={fieldSx}
                onChange={e => setNewPerm(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addCustom(); }}
                inputProps={{ dir: 'ltr' }} />
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={addCustom} disabled={!newPerm.trim()}
                sx={{ borderRadius: '10px', fontWeight: 700, bgcolor: BRAND.primary, '& .MuiButton-startIcon': { ml: .5, mr: -.25 } }}>إضافة</Button>
            </Paper>
          )}
        </Box>

        {/* الصلاحيات */}
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: '14px', p: 1.25, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField size="small" placeholder="بحث في الصلاحيات (مثال: اعتماد، فواتير، Invoice)..." value={query} onChange={e => setQuery(e.target.value)}
              sx={{ ...fieldSx, flex: 1, minWidth: 220 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
            <ToggleButtonGroup size="small" exclusive value={show} onChange={(_, v) => v && setShow(v)}
              sx={{ '& .MuiToggleButton-root': { px: 1.5, fontWeight: 700, borderRadius: '10px !important', border: `1px solid ${BORDER} !important`, mx: .25 },
                '& .Mui-selected': { bgcolor: `${BRAND.primarySoft} !important`, color: `${BRAND.primary} !important` } }}>
              <ToggleButton value="all">الكل</ToggleButton>
              <ToggleButton value="on">الممنوحة</ToggleButton>
              <ToggleButton value="off">غير الممنوحة</ToggleButton>
            </ToggleButtonGroup>
          </Paper>

          {/* التنقل السريع بين الأقسام */}
          {modules.length > 0 && (
            <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: '14px', p: 1, display: 'flex', gap: .75, flexWrap: 'wrap', alignItems: 'center', position: 'sticky', top: 72, zIndex: 2 }}>
              {modules.map(m => {
                const on = m.perms.filter(p => selected.has(p)).length;
                return (
                  <Chip key={m.key} onClick={() => jumpTo(m.key)} icon={m.icon}
                    label={`${m.name} ${on}/${m.perms.length}`}
                    sx={{ fontWeight: 700, bgcolor: on ? `${m.color}14` : '#f8fafc', color: on ? m.color : '#475569', border: `1px solid ${on ? `${m.color}55` : BORDER}`,
                      '& .MuiChip-icon': { color: 'inherit', fontSize: 17 } }} />
                );
              })}
              <Box sx={{ flex: 1 }} />
              <Tooltip title="فتح الكل"><IconButton size="small" onClick={expandAll}><UnfoldMoreIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="طي الكل"><IconButton size="small" onClick={collapseAll}><UnfoldLessIcon fontSize="small" /></IconButton></Tooltip>
            </Paper>
          )}

          {modules.length === 0 ? (
            <Paper elevation={0} sx={{ border: '1px dashed #cbd5e1', borderRadius: '14px', py: 5, textAlign: 'center', color: '#64748b' }}>لا توجد صلاحيات مطابقة</Paper>
          ) : modules.map(m => {
            const on = m.perms.filter(p => selected.has(p)).length;
            const all = on === m.perms.length, some = on > 0 && !all;
            const open = isOpen(m.key);
            return (
              <Paper key={m.key} id={`perm-mod-${m.key}`} elevation={0}
                sx={{ border: `1px solid ${on ? `${m.color}55` : BORDER}`, borderRadius: '16px', overflow: 'hidden', scrollMarginTop: 140 }}>
                {/* رأس القسم */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: .75, bgcolor: on ? `${m.color}0d` : '#fff', borderBottom: open ? `1px solid ${BORDER}` : 'none' }}>
                  <Checkbox checked={all} indeterminate={some} disabled={ro} onChange={() => setGroup(m.perms, !all)}
                    sx={{ '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: m.color } }} />
                  <Box sx={{ width: 34, height: 34, borderRadius: '10px', display: 'grid', placeItems: 'center', bgcolor: `${m.color}14`, color: m.color, '& svg': { fontSize: 19 } }}>{m.icon}</Box>
                  <Box onClick={() => toggleModule(m.key)} sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 15.5 }}>{m.name}</Typography>
                    <Typography sx={{ fontSize: 12, color: '#64748b' }}>{m.groups.map(([g]) => categoryName(g)).join(' • ')}</Typography>
                  </Box>
                  <Chip size="small" label={`${on}/${m.perms.length}`}
                    sx={{ height: 24, fontWeight: 800, fontSize: 12, bgcolor: on ? m.color : '#e2e8f0', color: on ? '#fff' : '#475569' }} />
                  <IconButton size="small" onClick={() => toggleModule(m.key)} disabled={searching}
                    sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>
                    <ExpandMoreIcon />
                  </IconButton>
                </Box>

                {/* مجموعات القسم */}
                <Collapse in={open} unmountOnExit>
                  <Box sx={{ p: 1.25, display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }, alignItems: 'start' }}>
                    {m.groups.map(([cat, perms]) => {
                      const gOn = perms.filter(p => selected.has(p)).length;
                      const gAll = gOn === perms.length, gSome = gOn > 0 && !gAll;
                      return (
                        <Box key={cat} sx={{ border: `1px solid ${gAll ? `${m.color}55` : BORDER}`, borderRadius: '12px', overflow: 'hidden', bgcolor: '#fff' }}>
                          <Box onClick={() => setGroup(perms, !gAll)}
                            sx={{ display: 'flex', alignItems: 'center', gap: .5, px: 1, py: .25, bgcolor: gAll ? `${m.color}0d` : '#fafbfc', borderBottom: `1px solid ${BORDER}`, cursor: ro ? 'default' : 'pointer' }}>
                            <Checkbox size="small" checked={gAll} indeterminate={gSome} disabled={ro} sx={{ '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: m.color } }} />
                            <Typography sx={{ fontWeight: 800, fontSize: 13.5, flex: 1 }}>{categoryName(cat)}</Typography>
                            <Typography sx={{ fontSize: 12, fontWeight: 800, color: gOn ? m.color : '#94a3b8' }}>{gOn}/{perms.length}</Typography>
                          </Box>
                          <Box sx={{ p: .5 }}>
                            {perms.map(p => (
                              <Box key={p} onClick={() => toggle(p)}
                                sx={{ display: 'flex', alignItems: 'center', gap: .5, px: .5, borderRadius: '8px', cursor: ro ? 'default' : 'pointer', '&:hover': { bgcolor: ro ? 'transparent' : '#f8fafc' } }}>
                                <Checkbox size="small" checked={selected.has(p)} disabled={ro} sx={{ '&.Mui-checked': { color: m.color } }} />
                                <Typography sx={{ fontSize: 13.5, fontWeight: 600, flex: 1, color: selected.has(p) ? '#0f172a' : '#64748b' }}>{actionName(p)}</Typography>
                                <Typography sx={{ fontSize: 11, color: '#94a3b8', direction: 'ltr' }}>{p}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Box>
      </Box>
    </Dialog>
  );
}